/**
 * OperatingHoursSelector Component (v2.9.170)
 * Allows admin to set global recording hours (from - to) or enable
 * 24/7 always-on mode. The backend treats `start == end` (in
 * particular `start = end = 0`) as the "always on" sentinel.
 */

import React, { useState } from 'react';
import { Clock, Check, X, Settings, RefreshCw, Infinity as InfinityIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Generate hours array 00-23
const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: `${i.toString().padStart(2, '0')}:00`
}));

const OperatingHoursSelector = ({ 
  currentStart, 
  currentEnd, 
  token,
  onUpdate 
}) => {
  // v2.9.170 — `start == end` or `(0, 0)` is the backend's 24/7
  // sentinel. Recognise that on initial render so the toggle starts
  // in the correct position.
  const isCurrentlyAlwaysOn = (
    (currentStart === currentEnd)
    || (currentStart === 0 && currentEnd === 0)
  );
  const [isOpen, setIsOpen] = useState(false);
  const [alwaysOn, setAlwaysOn] = useState(isCurrentlyAlwaysOn);
  const [startHour, setStartHour] = useState(
    isCurrentlyAlwaysOn ? 6 : currentStart
  );
  const [endHour, setEndHour] = useState(
    isCurrentlyAlwaysOn ? 21 : currentEnd
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    // v2.9.170 — when alwaysOn is checked we ignore the start/end
    // pickers and submit (0, 0) which the backend recognises as 24/7.
    const payloadStart = alwaysOn ? 0 : startHour;
    const payloadEnd = alwaysOn ? 0 : endHour;
    if (!alwaysOn && startHour >= endHour) {
      toast.error('Η ώρα έναρξης πρέπει να είναι πριν την ώρα λήξης');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API}/api/stream-monitor/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          operating_hours_start: payloadStart,
          operating_hours_end: payloadEnd
        })
      });

      if (res.ok) {
        toast.success(
          alwaysOn
            ? 'Πάντα Ενεργό (24/7)'
            : `Ώρες καταγραφής: ${startHour.toString().padStart(2, '0')}:00 - ${endHour.toString().padStart(2, '0')}:00`
        );
        setIsOpen(false);
        if (onUpdate) {
          onUpdate(payloadStart, payloadEnd);
        }
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Σφάλμα αποθήκευσης');
      }
    } catch (error) {
      toast.error('Σφάλμα σύνδεσης');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setAlwaysOn(isCurrentlyAlwaysOn);
    setStartHour(isCurrentlyAlwaysOn ? 6 : currentStart);
    setEndHour(isCurrentlyAlwaysOn ? 21 : currentEnd);
    setIsOpen(false);
  };

  // v2.9.170 — short trigger label: "24/7" when always-on, else range
  const triggerLabel = isCurrentlyAlwaysOn
    ? '24/7'
    : `${currentStart.toString().padStart(2, '0')}:00 - ${currentEnd.toString().padStart(2, '0')}:00`;

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium 
                   bg-amber-500/20 text-amber-600 dark:text-amber-400 
                   hover:bg-amber-500/30 transition-colors"
        title="Ρύθμιση ωρών καταγραφής"
        data-testid="operating-hours-trigger"
      >
        {isCurrentlyAlwaysOn ? (
          <InfinityIcon className="w-3.5 h-3.5" />
        ) : (
          <Clock className="w-3.5 h-3.5" />
        )}
        <span>{triggerLabel}</span>
        <Settings className="w-3 h-3 ml-1 opacity-60" />
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={handleCancel}
          />
          
          {/* Panel */}
          <div className="absolute top-full right-0 mt-2 z-50 
                          bg-white dark:bg-gray-800 rounded-xl shadow-xl 
                          border border-gray-200 dark:border-gray-700 
                          p-4 min-w-[280px]"
               data-testid="operating-hours-panel"
          >
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-amber-500" />
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Ώρες Καταγραφής
              </h3>
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Ορίστε τις ώρες που θα γίνεται αυτόματη καταγραφή για όλους τους σταθμούς.
            </p>

            {/* v2.9.170 — 24/7 toggle */}
            <label
              className="flex items-center justify-between gap-3 mb-4 p-2.5 rounded-lg
                         bg-gradient-to-r from-amber-50 to-orange-50
                         dark:from-amber-900/20 dark:to-orange-900/20
                         border border-amber-200/60 dark:border-amber-700/40
                         cursor-pointer hover:border-amber-300 transition-colors"
              data-testid="operating-hours-always-on-row"
            >
              <div className="flex items-center gap-2">
                <InfinityIcon className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    Πάντα Ενεργό (24/7)
                  </div>
                  <div className="text-[10px] text-gray-500 dark:text-gray-400">
                    Καταγραφή χωρίς ωράριο. Συνιστάται για σταθερά servers.
                  </div>
                </div>
              </div>
              <input
                type="checkbox"
                checked={alwaysOn}
                onChange={(e) => setAlwaysOn(e.target.checked)}
                className="w-4 h-4 accent-amber-500 cursor-pointer"
                data-testid="operating-hours-always-on-toggle"
              />
            </label>

            <div
              className={`flex items-center gap-3 mb-4 transition-opacity ${
                alwaysOn ? 'opacity-40 pointer-events-none' : 'opacity-100'
              }`}
            >
              {/* Start Hour */}
              <div className="flex-1">
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Από
                </label>
                <select
                  value={startHour}
                  onChange={(e) => setStartHour(parseInt(e.target.value))}
                  disabled={alwaysOn}
                  className="w-full px-3 py-2 rounded-lg text-sm
                             bg-gray-100 dark:bg-gray-700 
                             border border-gray-200 dark:border-gray-600
                             text-gray-900 dark:text-white
                             focus:ring-2 focus:ring-amber-500 focus:border-transparent
                             disabled:cursor-not-allowed"
                  data-testid="operating-hours-start"
                >
                  {HOURS.map(h => (
                    <option key={h.value} value={h.value}>{h.label}</option>
                  ))}
                </select>
              </div>

              <span className="text-gray-400 pt-5">→</span>

              {/* End Hour */}
              <div className="flex-1">
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Έως
                </label>
                <select
                  value={endHour}
                  onChange={(e) => setEndHour(parseInt(e.target.value))}
                  disabled={alwaysOn}
                  className="w-full px-3 py-2 rounded-lg text-sm
                             bg-gray-100 dark:bg-gray-700 
                             border border-gray-200 dark:border-gray-600
                             text-gray-900 dark:text-white
                             focus:ring-2 focus:ring-amber-500 focus:border-transparent
                             disabled:cursor-not-allowed"
                  data-testid="operating-hours-end"
                >
                  {HOURS.map(h => (
                    <option key={h.value} value={h.value}>{h.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Validation warning */}
            {!alwaysOn && startHour >= endHour && (
              <div className="text-xs text-red-500 mb-3 flex items-center gap-1">
                <X className="w-3 h-3" />
                Η ώρα έναρξης πρέπει να είναι πριν την ώρα λήξης
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2">
              <Button
                onClick={handleCancel}
                size="sm"
                variant="ghost"
                className="text-gray-500"
              >
                Άκυρο
              </Button>
              <Button
                onClick={handleSave}
                size="sm"
                disabled={saving || (!alwaysOn && startHour >= endHour)}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="operating-hours-save"
              >
                {saving ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-1" />
                    Αποθήκευση
                  </>
                )}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OperatingHoursSelector;
